import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useService } from '@/app/di';
import { toast } from '@/shared/lib/toast';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import {
  INVOICE_SERVICE,
  type IInvoiceRepository,
} from '../infrastructure/invoice.service';

function useRepo(): IInvoiceRepository {
  return useService(INVOICE_SERVICE);
}

export const invoiceKeys = {
  all: ['invoices'] as const,
  list: (params?: Record<string, unknown>) =>
    [...invoiceKeys.all, 'list', params] as const,
  detail: (id: string) => [...invoiceKeys.all, 'detail', id] as const,
  uninvoicedSteps: (patientId: string, appointmentId?: string) =>
    [...invoiceKeys.all, 'uninvoiced', patientId, appointmentId ?? 'all'] as const,
  stats: () => [...invoiceKeys.all, 'stats'] as const,
};

export function useInvoiceList(
  params?: {
    page?: number;
    status?: string;
    search?: string;
    patientId?: string;
  },
  options?: { enabled?: boolean; keepPrevious?: boolean },
) {
  const repo = useRepo();
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () => repo.list(params),
    staleTime: QUERY_STALE_MS.HOT,
    enabled: options?.enabled ?? true,
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  });
}

export function usePatientInvoices(patientId: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: invoiceKeys.list({ patientId }),
    queryFn: () => repo.list({ patientId }),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function useInvoiceDetail(id: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: invoiceKeys.detail(id!),
    queryFn: () => repo.getDetail(id!),
    enabled: !!id,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function useUninvoicedSteps(
  patientId: string | undefined,
  appointmentId?: string,
) {
  const repo = useRepo();
  return useQuery({
    queryKey: invoiceKeys.uninvoicedSteps(patientId!, appointmentId),
    queryFn: () => repo.getUninvoicedSteps(patientId!, appointmentId),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function useInvoiceStats() {
  const repo = useRepo();
  return useQuery({
    queryKey: invoiceKeys.stats(),
    queryFn: () => repo.getStats(),
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  const repo = useRepo();

  return useMutation({
    mutationFn: (input: Parameters<IInvoiceRepository['createInvoice']>[0]) =>
      repo.createInvoice(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success('Invoice created');
    },
    onError: (err: Error) => {
      toast.failure(err.message ?? 'Failed to create invoice');
    },
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  const repo = useRepo();

  return useMutation({
    mutationFn: (input: {
      id: string;
      amountPaise: number;
      method: 'upi' | 'cash' | 'card' | 'neft' | 'insurance';
      paymentDate?: string;
      reference?: string;
      note?: string;
    }) =>
      repo.recordPayment(input.id, {
        amountPaise: input.amountPaise,
        method: input.method,
        paymentDate: input.paymentDate,
        reference: input.reference,
        note: input.note,
      }),
    onSuccess: () => {
      // Invalidate all invoice queries (detail + every list param shape including patientId).
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success('Payment recorded');
    },
    onError: (err: Error) => {
      toast.failure(err.message ?? 'Failed to record payment');
    },
  });
}

export function useCancelPayment() {
  const queryClient = useQueryClient();
  const repo = useRepo();

  return useMutation({
    mutationFn: (input: { invoiceId: string; paymentId: string }) =>
      repo.cancelPayment(input.invoiceId, input.paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success('Payment cancelled');
    },
    onError: (err: Error) => {
      toast.failure(err.message ?? 'Failed to cancel payment');
    },
  });
}

export function useEditInvoiceContents() {
  const queryClient = useQueryClient();
  const repo = useRepo();

  return useMutation({
    mutationFn: (input: {
      id: string;
      discountPaise?: number;
      invoiceDate?: string;
      removeLineItemIds?: string[];
    }) =>
      repo.editInvoiceContents(input.id, {
        discountPaise: input.discountPaise,
        invoiceDate: input.invoiceDate,
        removeLineItemIds: input.removeLineItemIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success('Invoice updated');
    },
    onError: (err: Error) => {
      toast.failure(err.message ?? 'Failed to update invoice');
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();
  const repo = useRepo();

  return useMutation({
    mutationFn: (id: string) => repo.voidInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      toast.success('Invoice cancelled');
    },
    onError: (err: Error) => {
      toast.failure(err.message ?? 'Failed to cancel invoice');
    },
  });
}
